import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { Target, MapPin, Radio, Building, Send, ChevronDown, ChevronUp, Phone, Mail, Sparkles, ArrowUpRight, AlertTriangle, Eye } from 'lucide-react';
import SearchBar, { matches } from '../components/app/SearchBar';

const streetViewUrl = (lat, lng) => `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;

const CoverageBadge = ({ q }) => {
  const map = {
    baja: { label: 'COBERTURA BAJA', color: 'bg-red-500/15 text-red-400 border-red-500/30', emoji: '⚡' },
    media: { label: 'COBERTURA MEDIA', color: 'bg-orange-500/15 text-orange-400 border-orange-500/30', emoji: '◯' },
    alta: { label: 'COBERTURA ALTA', color: 'bg-[#A3FF12]/15 text-[#A3FF12] border-[#A3FF12]/30', emoji: '✓' },
  }[q] || { label: q?.toUpperCase() || 'N/D', color: 'bg-white/5 text-white/60 border-white/10', emoji: '?' };
  return <span className={`text-[10px] font-black px-2 py-1 rounded-full border ${map.color}`}>{map.emoji} {map.label}</span>;
};

const PriorityBadge = ({ p }) => {
  const map = { L1: 'bg-red-500/15 text-red-400 border-red-500/30', L2: 'bg-orange-500/15 text-orange-400 border-orange-500/30', L3: 'bg-white/5 text-white/60 border-white/10' };
  return <span className={`text-[10px] font-black px-2 py-1 rounded-full border ${map[p] || map.L3}`}>{p}</span>;
};

const Ofrecimientos = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [coverageFilter, setCoverageFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/offerings');
      setGroups(r.data);
      // Auto-expand top groups (low coverage)
      const init = {};
      r.data.slice(0, 3).forEach((g) => { init[g.municipality] = true; });
      setOpen(init);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (m) => setOpen({ ...open, [m]: !open[m] });

  const filteredGroups = groups.map((g) => ({
    ...g,
    candidates: g.candidates.filter((c) =>
      (!coverageFilter || c.coverage_quality === coverageFilter) &&
      matches(search, c.name, c.address, g.municipality, g.province)
    ),
  })).filter((g) => g.candidates.length > 0);

  const totalCandidates = filteredGroups.reduce((s, g) => s + g.candidates.length, 0);
  const totalLow = groups.reduce((s, g) => s + g.low_coverage_count, 0);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6 lg:mb-8">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12] mb-2">OFRECIMIENTOS</div>
          <h1 className="text-3xl lg:text-4xl font-black text-white">Candidatos por zona</h1>
          <p className="text-white/50 mt-2 text-sm lg:text-base">Listado de emplazamientos agrupados por municipio, priorizando zonas con baja cobertura para ofrecer al operador.</p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 lg:p-5">
          <Target className="text-[#A3FF12] mb-2" size={20}/>
          <div className="text-2xl lg:text-3xl font-black text-white">{groups.length}</div>
          <div className="text-white/60 text-xs lg:text-sm">Zonas activas</div>
        </div>
        <div className="bg-[#0a0a0a] border border-red-500/20 rounded-2xl p-4 lg:p-5">
          <AlertTriangle className="text-red-400 mb-2" size={20}/>
          <div className="text-2xl lg:text-3xl font-black text-white">{totalLow}</div>
          <div className="text-white/60 text-xs lg:text-sm">Sitios con baja cobertura</div>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 lg:p-5">
          <MapPin className="text-[#A3FF12] mb-2" size={20}/>
          <div className="text-2xl lg:text-3xl font-black text-white">{totalCandidates}</div>
          <div className="text-white/60 text-xs lg:text-sm">Candidatos totales</div>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 lg:p-5">
          <Building className="text-[#A3FF12] mb-2" size={20}/>
          <div className="text-2xl lg:text-3xl font-black text-white">
            {groups[0]?.avg_building_height ? `${groups[0].avg_building_height}m` : '–'}
          </div>
          <div className="text-white/60 text-xs lg:text-sm">Altura media top zona</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { v: '', label: 'Todos' },
          { v: 'baja', label: '⚡ Solo baja cobertura' },
          { v: 'media', label: 'Solo media' },
          { v: 'alta', label: 'Solo alta' },
        ].map((f) => (
          <button key={f.v} onClick={() => setCoverageFilter(f.v)}
            className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
              coverageFilter === f.v ? 'bg-[#A3FF12] text-black border-[#A3FF12]' : 'border-white/10 text-white/70 hover:border-white/30'
            }`}>{f.label}</button>
        ))}
      </div>
      <div className="mb-6"><SearchBar value={search} onChange={setSearch} placeholder="Buscar por emplazamiento, dirección o municipio…" testId="offerings-search"/></div>

      {loading && <div className="text-white/40 text-sm">Cargando ofrecimientos...</div>}

      {!loading && filteredGroups.length === 0 && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-12 text-center">
          <p className="text-white/50">No hay emplazamientos guardados para ofrecer todavía.</p>
          <p className="text-white/40 text-sm mt-2">Ve a <strong className="text-[#A3FF12]">Prospección</strong>, busca una dirección y guarda emplazamientos para que aparezcan aquí.</p>
        </div>
      )}

      <div className="space-y-4">
        {filteredGroups.map((g) => {
          const expanded = !!open[g.municipality];
          const lowPct = g.total > 0 ? Math.round((g.low_coverage_count / g.total) * 100) : 0;
          return (
            <div key={g.municipality} className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
              {/* Group header */}
              <button onClick={() => toggle(g.municipality)} className="w-full flex items-center justify-between gap-4 px-5 lg:px-6 py-4 lg:py-5 hover:bg-white/[0.02] transition-colors text-left">
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${g.low_coverage_count > 0 ? 'bg-red-500/10 text-red-400' : 'bg-[#A3FF12]/10 text-[#A3FF12]'}`}>
                    <MapPin size={22}/>
                  </div>
                  <div className="min-w-0">
                    <div className="text-white font-black text-lg truncate">{g.municipality}</div>
                    <div className="text-white/50 text-xs truncate">
                      {g.province} · {g.total} emplazamiento{g.total > 1 ? 's' : ''} · Altura media {g.avg_building_height || '–'}m
                    </div>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
                  {g.low_coverage_count > 0 && (
                    <span className="bg-red-500/15 text-red-400 border border-red-500/30 text-xs font-black px-3 py-1.5 rounded-full">
                      {g.low_coverage_count} BAJA ({lowPct}%)
                    </span>
                  )}
                  {g.medium_coverage_count > 0 && (
                    <span className="bg-orange-500/15 text-orange-400 border border-orange-500/30 text-xs font-bold px-3 py-1.5 rounded-full">
                      {g.medium_coverage_count} MEDIA
                    </span>
                  )}
                  {g.high_coverage_count > 0 && (
                    <span className="bg-[#A3FF12]/15 text-[#A3FF12] border border-[#A3FF12]/30 text-xs font-bold px-3 py-1.5 rounded-full">
                      {g.high_coverage_count} ALTA
                    </span>
                  )}
                </div>
                {expanded ? <ChevronUp size={20} className="text-white/40 flex-shrink-0"/> : <ChevronDown size={20} className="text-white/40 flex-shrink-0"/>}
              </button>

              {/* Mobile pills */}
              <div className="flex sm:hidden flex-wrap gap-2 px-5 pb-4 -mt-1">
                {g.low_coverage_count > 0 && <span className="bg-red-500/15 text-red-400 border border-red-500/30 text-[10px] font-black px-2 py-1 rounded-full">{g.low_coverage_count} BAJA</span>}
                {g.medium_coverage_count > 0 && <span className="bg-orange-500/15 text-orange-400 border border-orange-500/30 text-[10px] font-bold px-2 py-1 rounded-full">{g.medium_coverage_count} MEDIA</span>}
                {g.high_coverage_count > 0 && <span className="bg-[#A3FF12]/15 text-[#A3FF12] border border-[#A3FF12]/30 text-[10px] font-bold px-2 py-1 rounded-full">{g.high_coverage_count} ALTA</span>}
              </div>

              {expanded && (
                <div className="border-t border-white/5 px-3 lg:px-4 py-3 space-y-2 bg-black/30">
                  {g.candidates.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 bg-[#0a0a0a] border border-white/5 rounded-xl p-3 hover:border-[#A3FF12]/30 transition-colors">
                      <div className="flex-shrink-0">
                        <Radio className={`${c.coverage_quality === 'baja' ? 'text-red-400' : c.coverage_quality === 'media' ? 'text-orange-400' : 'text-[#A3FF12]'}`} size={20}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-bold truncate">{c.name}</span>
                          <CoverageBadge q={c.coverage_quality}/>
                          <PriorityBadge p={c.priority}/>
                        </div>
                        <div className="text-white/40 text-xs truncate mt-0.5">{c.address}</div>
                        <div className="text-white/50 text-xs mt-1 flex items-center gap-3 flex-wrap">
                          {c.building_height_m && <span><Building size={11} className="inline mr-1"/>{c.building_height_m}m</span>}
                          {c.lat && <span className="text-white/30">{c.lat.toFixed(4)}, {c.lng.toFixed(4)}</span>}
                          <span className="uppercase text-white/40">{c.status}</span>
                        </div>
                      </div>
                      <a href={streetViewUrl(c.lat, c.lng)} target="_blank" rel="noreferrer"
                        className="text-white/40 hover:text-[#A3FF12] p-2 flex-shrink-0" title="Street View">
                        <Eye size={16}/>
                      </a>
                      <a href={`https://www.google.com/maps?q=${c.lat},${c.lng}`} target="_blank" rel="noreferrer"
                        className="text-white/40 hover:text-[#A3FF12] p-2 flex-shrink-0" title="Ver en Google Maps">
                        <ArrowUpRight size={16}/>
                      </a>
                    </div>
                  ))}
                  <button className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#A3FF12]/10 border border-[#A3FF12]/30 text-[#A3FF12] py-2.5 rounded-xl text-sm font-bold hover:bg-[#A3FF12]/20 transition-colors">
                    <Send size={14}/> Generar ofrecimiento para esta zona
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Ofrecimientos;
